import { useContext, useMemo } from 'react';

import AppContext from 'AppContext';
import { IOrder } from 'types/order';
import { OfferTemplateKind } from 'types/graphql/globalTypes';
import {
    getAvailableConsumptionPoints,
    getConsumptionPointsFromOrders,
    mergeConsumptionPoints,
    IConsumptionPoint,
    CONSUMPTION_POINT_FILTER_ALL,
} from 'services/consumptionPointService';

// Extra header height needed for the filter's line below the clock (added only when it's shown).
export const HEADER_FILTER_EXTRA = 22;

interface IConsumptionPointFilter {
    consumptionPoints: IConsumptionPoint[];
    showConsumptionPointFilter: boolean;
    activeConsumptionPointId: string;
    setConsumptionPointFilterId: (id: string) => void;
}

// Shared state for the consumption-point filter, used by both the header (to render the control)
// and Content (to narrow the displayed orders). Options = the selected offers' consumption points
// unioned with any found on the loaded orders (so a point removed/renamed on an offer stays
// filterable). Shown when there are >= 2 points, or >= 1 alongside a legacy offer.
export function useConsumptionPointFilter(): IConsumptionPointFilter {
    const { offers, orders: ordersList, consumptionPointFilterId, setConsumptionPointFilterId } = useContext(
        AppContext.Context
    );

    const consumptionPoints = useMemo(
        () =>
            mergeConsumptionPoints(
                getAvailableConsumptionPoints(offers),
                getConsumptionPointsFromOrders((Object.values(ordersList) as IOrder[][]).flat())
            ),
        [offers, ordersList]
    );

    const hasLegacyOffer = (offers ?? []).some((offer) => offer.kind !== OfferTemplateKind.OFFER_BUILDER);
    const showConsumptionPointFilter =
        consumptionPoints.length >= 2 || (consumptionPoints.length >= 1 && hasLegacyOffer);
    const activeConsumptionPointId =
        showConsumptionPointFilter && consumptionPoints.some((cp) => cp.id === consumptionPointFilterId)
            ? consumptionPointFilterId
            : CONSUMPTION_POINT_FILTER_ALL;

    return {
        consumptionPoints,
        showConsumptionPointFilter,
        activeConsumptionPointId,
        setConsumptionPointFilterId: (id: string) => setConsumptionPointFilterId(id),
    };
}
